import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Phone,
  Shuffle,
  Video,
  ShieldCheck,
  Activity,
  Wand2,
  MessageCircle,
  Bot,
  Headphones,
  Mic,
  TrendingUp,
  Layers,
  GitBranch,
  Cpu,
  BarChart3,
  ArrowRight,
  Award,
  Wrench,
  Building2,
  MapPin,
  Package,
  Search,
  PenTool,
  Truck,
  Settings,
} from "lucide-react";
import ContactForm from "@/components/ContactForm";
import Partner from "@/components/Partner";
import {
  PRODUCT_ROWS,
  scrollToHash,
  useDocumentTitle,
} from "@/lib/shared";

const CONNECT_CARDS = [
  {
    Icon: ShieldCheck,
    title: "Control Total",
    desc: "Panel centralizado para gestionar usuarios y licencias.",
  },
  {
    Icon: Activity,
    title: "Calidad Garantizada",
    desc: "Herramientas de monitoreo en tiempo real (tecnología AudioCodes Live).",
  },
  {
    Icon: Wand2,
    title: "Simplicidad",
    desc: "Wizards de configuración que eliminan la complejidad técnica.",
  },
];

const VOICE_AI_CARDS = [
  {
    Icon: MessageCircle,
    title: "Dale voz a tu Bot",
    desc: "Conectamos tus chatbots actuales (WhatsApp/Web) a la red telefónica con voz humana natural.",
  },
  {
    Icon: Bot,
    title: "Bots Transaccionales",
    desc: "Asistentes autónomos que agendan citas, procesan pagos o consultan saldos 24/7.",
  },
  {
    Icon: Headphones,
    title: "Agent Assist",
    desc: "La IA escucha la llamada en vivo y ayuda a tus agentes sugiriendo respuestas o buscando información al instante.",
  },
];

const INSIGHTS_CARDS = [
  {
    Icon: Mic,
    title: "Grabación Unificada",
    desc: "Capturamos llamadas con clientes externos (PSTN) y conversaciones internas entre colaboradores.",
  },
  {
    Icon: TrendingUp,
    title: "Análisis de Sentimiento",
    desc: "Detectamos emociones y palabras clave para mejorar la calidad y asegurar el cumplimiento legal.",
  },
];

const CC_CARDS = [
  {
    Icon: Layers,
    title: "Omnicanalidad Nativa",
    desc: "Los agentes gestionan interacciones de voz y canales digitales (chat, correo) simultáneamente desde la misma interfaz, integrando en vivo los datos de tu CRM.",
  },
  {
    Icon: GitBranch,
    title: "Enrutamiento por Habilidades",
    desc: "Las interacciones se enrutan automáticamente al agente más adecuado.",
  },
  {
    Icon: Cpu,
    title: "IA Conversacional Integrada",
    desc: "Diseño AI-first que automatiza el IVR y brinda autoservicio inteligente.",
  },
  {
    Icon: BarChart3,
    title: "Control y Reportes",
    desc: "Tableros en tiempo real para supervisores.",
  },
];

const Hero = () => (
  <section className="cn-section cn-hero" id="inicio" data-testid="hero-section">
    <div className="cn-hero-bg" aria-hidden="true">
      <span className="cn-hero-overlay" />
      <span className="cn-hero-blob cn-hero-blob-1" />
      <span className="cn-hero-blob cn-hero-blob-2" />
      <span className="cn-hero-blob cn-hero-blob-3" />
      <span className="cn-hero-grid" />
    </div>
    <div className="cn-hero-fade" aria-hidden="true" />
    <div className="cn-hero-split">
      <div className="cn-hero-col-left">
        <span
          className="cn-eyebrow-pill"
          data-reveal
          data-testid="hero-eyebrow"
        >
          CLOUD VOICE ENABLEMENT PLATFORM
        </span>
        <h1
          className="cn-h1 cn-h1-split"
          data-reveal
          data-testid="hero-headline"
        >
          Convierte tu solución UCaaS en el centro de telefonía inteligente
          de tu empresa.
        </h1>
        <p className="cn-subhead cn-subhead-split" data-reveal data-testid="hero-subhead">
          Habilitamos tus líneas telefónicas actuales para que funcionen
          dentro de Teams, Zoom, Google Voice o Webex con control total,
          seguridad y el poder de la Inteligencia Artificial.
        </p>
        <div className="cn-hero-ctas cn-hero-ctas-left" data-reveal>
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              scrollToHash("contacto");
            }}
            className="cn-btn cn-btn-gradient"
            data-testid="hero-cta-demo"
          >
            Solicitar Evaluación Técnica
          </a>
          <a
            href="#conector"
            onClick={(e) => {
              e.preventDefault();
              scrollToHash("conector");
            }}
            className="cn-btn cn-btn-outline"
            data-testid="hero-cta-soluciones"
          >
            Ver Soluciones
          </a>
        </div>
      </div>
      <div className="cn-hero-col-right" data-reveal>
        <div className="cn-hero-visual-card">
          <img
            src="/logos/ISOTIPO.png"
            alt="Caruna"
            className="cn-hero-visual-logo"
            loading="eager"
          />
          <div className="cn-hero-visual-badges">
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("contacto");
              }}
              className="cn-hero-badge"
              data-testid="hero-visual-badge-eval"
            >
              Solicitar Evaluación Técnica
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ConectorSection = () => {
  const flow = [
    { Icon: Phone, title: "Tu Telefonía Actual", variant: "white" },
    { Icon: Shuffle, title: "Caruna", variant: "gradient" },
    { Icon: Video, title: "Teams / Zoom / Webex", variant: "white" },
  ];
  return (
    <section
      className="cn-section cn-conector-section"
      id="conector"
      data-testid="conector-section"
    >
      <div className="cn-container">
        <div className="cn-conector-split">
          <div className="cn-conector-dark-card" data-reveal>
            <img
              src="/logos/LOGOTIPO-NEGATIVO.png"
              alt="Caruna"
              className="cn-conector-iso"
              loading="lazy"
            />
            <p className="cn-conector-tagline">Cloud Voice Enablement Platform</p>
          </div>
          <div className="cn-conector-text">
            <p className="cn-eyebrow" data-reveal>
              NUESTRA IDENTIDAD
            </p>
            <h2 className="cn-h2 cn-h2-conector" data-reveal data-testid="conector-headline">
              Conectamos tu infraestructura actual con el futuro de la
              comunicación.
            </h2>
            <p className="cn-conector-body" data-reveal>
              No reemplazamos tu telefonía ni actuamos como un proveedor de
              líneas; somos el puente tecnológico que permite que tus números
              actuales se integren perfectamente con tus plataformas de
              colaboración. Caruna añade la capa de gestión y seguridad que
              las nubes públicas no ofrecen de forma nativa.
            </p>
          </div>
        </div>
        <div className="cn-flow-horizontal" data-testid="conector-flow">
          {flow.map((b, i) => (
            <div
              key={b.title}
              className="cn-hflow-item"
              data-reveal
              style={{ "--cn-delay": `${i * 0.12}s` }}
            >
              <div
                className={`cn-hflow-box cn-hflow-${b.variant}`}
                data-testid={`conector-box-${i}`}
              >
                <b.Icon size={28} strokeWidth={1.8} className="cn-hflow-icon" />
                <span className="cn-hflow-title">{b.title}</span>
              </div>
              {i < flow.length - 1 && (
                <ArrowRight
                  className="cn-hflow-arrow"
                  size={28}
                  strokeWidth={2.2}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


/**
 * ProductPreviewSection (home) — estructura de 4 secciones apiladas
 * definida por el cliente con estilos inline: eyebrow + headline + descripción
 * + grid de tarjetas blancas (barra superior en degradado) + botón pill.
 */
const ProductPreviewSection = ({
  id,
  bg,
  eyebrow,
  headline,
  description,
  columns,
  cards,
  gridMaxWidth,
  ctaLabel,
  ctaHref,
  testId,
}) => (
  <section
    id={id}
    data-testid={`${testId}-section`}
    className="cn-preview-section"
    style={{
      background: bg,
      padding: "80px 24px",
      width: "100%",
    }}
  >
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      <p
        data-reveal
        className="cn-preview-eyebrow"
        style={{
          textAlign: "center",
          fontSize: "12px",
          letterSpacing: "2px",
          color: "#8A9A94",
          textTransform: "uppercase",
          marginBottom: "16px",
        }}
      >
        {eyebrow}
      </p>

      <h2
        data-reveal
        data-testid={`${testId}-headline`}
        className="cn-preview-headline"
        style={{
          textAlign: "center",
          fontSize: "44px",
          fontWeight: 900,
          color: "#1D1D1B",
          textTransform: "uppercase",
          letterSpacing: "-0.5px",
          marginBottom: "20px",
        }}
      >
        {headline}
      </h2>

      <p
        data-reveal
        className="cn-preview-desc"
        style={{
          textAlign: "center",
          fontSize: "17px",
          color: "#4A5A54",
          maxWidth: "680px",
          margin: "0 auto 48px",
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>

      <div
        className="cn-preview-grid"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: "20px",
          marginBottom: "40px",
          maxWidth: gridMaxWidth || "100%",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {cards.map((c, i) => (
          <div
            key={c.title}
            data-reveal
            data-testid={`${testId}-card-${i}`}
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                height: "6px",
                background: "linear-gradient(90deg, #0C7E6C, #8BC054)",
              }}
            />
            <div style={{ padding: "24px" }}>
              <c.Icon size={24} strokeWidth={1.9} color="#3D9E6A" />
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                  marginTop: "12px",
                  marginBottom: "8px",
                }}
              >
                {c.title}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6E6E73",
                  lineHeight: 1.6,
                }}
              >
                {c.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center" }}>
        <Link
          to={ctaHref}
          data-testid={`${testId}-learn-more`}
          style={{
            display: "inline-block",
            background: "#1D1D1B",
            color: "#FFFFFF",
            borderRadius: "999px",
            padding: "14px 40px",
            fontSize: "16px",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  </section>
);

const ConnectSection = () => (
  <ProductPreviewSection
    id="connect"
    bg="#E8EDEB"
    eyebrow="GESTIÓN Y CONTROL"
    headline="CARUNA CONNECT"
    description="Administra, Monitorea y Asegura tu voz desde un solo lugar."
    columns={3}
    cards={CONNECT_CARDS}
    ctaLabel="Conocer más sobre Connect →"
    ctaHref="/connect"
    testId="connect"
  />
);

const VoiceAISection = () => (
  <ProductPreviewSection
    id="voice-ai"
    bg="#DDE8E4"
    eyebrow="AUTOMATIZACIÓN E INTELIGENCIA"
    headline="INTELIGENCIA ARTIFICIAL"
    description="Inteligencia Artificial: dale voz a tu estrategia digital."
    columns={3}
    cards={VOICE_AI_CARDS}
    ctaLabel="Conocer más sobre Voice AI →"
    ctaHref="/voice-ai"
    testId="voice-ai"
  />
);

const InsightsSection = () => (
  <ProductPreviewSection
    id="insights"
    bg="#E8EDEB"
    eyebrow="GRABACIÓN Y ANALÍTICA"
    headline="CAPTURA EL VALOR DE CADA CONVERSACIÓN"
    description="Captura el valor de cada conversación, interna o externa."
    columns={2}
    cards={INSIGHTS_CARDS}
    gridMaxWidth="740px"
    ctaLabel="Conocer más sobre Insights →"
    ctaHref="/insights"
    testId="insights"
  />
);

const ContactCenterSection = () => (
  <ProductPreviewSection
    id="contact-center"
    bg="#DDE8E4"
    eyebrow="CONTACT CENTER OMNICANAL"
    headline="CONVIERTE MICROSOFT TEAMS EN UN CONTACT CENTER"
    description="Transforma tu operación de atención al cliente sin integraciones complejas ni cambiar de ventana. Nuestra plataforma certificada para Teams (basada en Voca CIC) ofrece una experiencia de pantalla única para tus agentes."
    columns={2}
    cards={CC_CARDS}
    ctaLabel="Conocer más sobre Contact Center →"
    ctaHref="/contact-center"
    testId="cc"
  />
);

const NOSOTROS_PILLARS = [
  {
    Icon: Award,
    title: "Experiencia Sólida",
    desc: "Más de una década especializada en soluciones de voz y comunicaciones empresariales.",
  },
  {
    Icon: Wrench,
    title: "Capacidad Técnica",
    desc: "Ingeniería certificada para el diseño, implementación, integración híbrida y soporte especializado.",
  },
  {
    Icon: Building2,
    title: "Experiencia Carrier & Enterprise",
    desc: "Arquitecturas de alta disponibilidad, SBC, SIP, Microsoft Teams y comunicaciones cloud.",
  },
  {
    Icon: MapPin,
    title: "Operación Local",
    desc: "Equipo en México, soporte 24/7 y acompañamiento durante todo el ciclo de vida del proyecto.",
  },
];

const PORTAFOLIO_ITEMS = [
  "Habilitación de Teams Phone complementada con Direct Routing",
  "Contact Center",
  "Recording & Analytics",
  "Teléfonos y Salas de Conferencias (Certificados)",
  "Conversión voz PSTN a datos",
];

const NosotrosSection = () => (
  <section
    id="nosotros"
    data-testid="nosotros-section"
    className="cn-preview-section"
    style={{
      background: "#F5F7F6",
      padding: "96px 24px",
      width: "100%",
    }}
  >
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      <p
        data-reveal
        className="cn-preview-eyebrow"
        style={{
          textAlign: "center",
          fontSize: "12px",
          letterSpacing: "2px",
          color: "#8A9A94",
          textTransform: "uppercase",
          marginBottom: "16px",
        }}
      >
        NOSOTROS
      </p>
      <h2
        data-reveal
        data-testid="nosotros-headline"
        className="cn-preview-headline"
        style={{
          textAlign: "center",
          fontSize: "44px",
          fontWeight: 900,
          color: "#1D1D1B",
          textTransform: "uppercase",
          letterSpacing: "-0.5px",
          marginBottom: "20px",
        }}
      >
        ESPECIALIZACIÓN EN VOZ
      </h2>
      <p
        data-reveal
        className="cn-preview-desc"
        style={{
          textAlign: "center",
          fontSize: "17px",
          color: "#4A5A54",
          maxWidth: "720px",
          margin: "0 auto 56px",
          lineHeight: 1.6,
        }}
      >
        Somos un equipo especializado en habilitar telefonía empresarial de
        última generación, con foco en Microsoft Teams, SBC y comunicaciones
        cloud. Estos son los pilares que nos diferencian.
      </p>

      <div
        className="cn-preview-grid cn-nosotros-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        {NOSOTROS_PILLARS.map((p, i) => (
          <div
            key={p.title}
            data-reveal
            data-testid={`nosotros-pillar-${i}`}
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                height: "6px",
                background: "linear-gradient(90deg, #0C7E6C, #8BC054)",
              }}
            />
            <div style={{ padding: "24px" }}>
              <p.Icon size={26} strokeWidth={1.9} color="#3D9E6A" />
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#1D1D1B",
                  marginTop: "14px",
                  marginBottom: "8px",
                }}
              >
                {p.title}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6E6E73",
                  lineHeight: 1.6,
                }}
              >
                {p.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div
        data-reveal
        data-testid="nosotros-portafolio"
        className="cn-nosotros-portafolio"
        style={{
          background: "#FFFFFF",
          borderRadius: "20px",
          padding: "32px",
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          display: "grid",
          gridTemplateColumns: "220px 1fr",
          gap: "32px",
          alignItems: "start",
        }}
      >
        <div>
          <Package size={30} strokeWidth={1.9} color="#3D9E6A" />
          <h3
            style={{
              fontSize: "20px",
              fontWeight: 800,
              color: "#1D1D1B",
              marginTop: "12px",
              marginBottom: "6px",
            }}
          >
            Portafolio Completo
          </h3>
          <p
            style={{
              fontSize: "13px",
              color: "#6E6E73",
              lineHeight: 1.55,
            }}
          >
            Cobertura end-to-end para tu estrategia de voz empresarial.
          </p>
        </div>
        <ul
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "12px 24px",
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {PORTAFOLIO_ITEMS.map((item) => (
            <li
              key={item}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                fontSize: "15px",
                color: "#1D1D1B",
                lineHeight: 1.5,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flex: "0 0 8px",
                  width: "8px",
                  height: "8px",
                  marginTop: "8px",
                  borderRadius: "999px",
                  background: "linear-gradient(135deg, #0C7E6C, #8BC054)",
                }}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const WORK_PHASES = [
  {
    n: "01",
    Icon: Search,
    tag: "DISCOVER",
    desc: "Evaluación técnica de infraestructura, PSTN, PBX legado y gobernanza.",
  },
  {
    n: "02",
    Icon: PenTool,
    tag: "DESIGN",
    desc: "Arquitectura híbrida, estrategia de coexistencia y optimización de dimensionamiento.",
  },
  {
    n: "03",
    Icon: Truck,
    tag: "DELIVER",
    desc: "Provisioning en la nube, portabilidad numérica y migración sin interrupciones.",
  },
  {
    n: "04",
    Icon: Settings,
    tag: "OPERATE",
    desc: "Monitoreo 24/7, soporte continuo de voz y evolución con Voice AI.",
  },
];

const ModeloTrabajoSection = () => (
  <section
    id="modelo-trabajo"
    data-testid="modelo-trabajo-section"
    className="cn-preview-section"
    style={{
      background: "#0D1F1A",
      padding: "96px 24px",
      width: "100%",
    }}
  >
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      <p
        data-reveal
        className="cn-preview-eyebrow"
        style={{
          textAlign: "center",
          fontSize: "12px",
          letterSpacing: "2px",
          color: "#8BC054",
          textTransform: "uppercase",
          marginBottom: "16px",
        }}
      >
        MODELO DE TRABAJO CONJUNTO
      </p>
      <h2
        data-reveal
        data-testid="modelo-trabajo-headline"
        className="cn-preview-headline"
        style={{
          textAlign: "center",
          fontSize: "44px",
          fontWeight: 900,
          color: "#FFFFFF",
          textTransform: "uppercase",
          letterSpacing: "-0.5px",
          marginBottom: "20px",
        }}
      >
        CÓMO COLABORAMOS CONTIGO
      </h2>
      <p
        data-reveal
        className="cn-preview-desc"
        style={{
          textAlign: "center",
          fontSize: "17px",
          color: "#B7C4BE",
          maxWidth: "720px",
          margin: "0 auto 56px",
          lineHeight: 1.6,
        }}
      >
        Un proceso de cuatro fases probado con clientes carrier y enterprise
        para llevar tu telefonía a la nube sin fricciones.
      </p>

      <div
        className="cn-preview-grid cn-modelo-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px",
        }}
      >
        {WORK_PHASES.map((p, i) => (
          <div
            key={p.tag}
            data-reveal
            data-testid={`modelo-phase-${i}`}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "16px",
              padding: "28px 24px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: "linear-gradient(90deg, #0C7E6C, #8BC054)",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "18px",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "2px",
                  color: "#8BC054",
                }}
              >
                {p.n}
              </span>
              <p.Icon size={22} strokeWidth={1.8} color="#8BC054" />
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: 800,
                color: "#FFFFFF",
                letterSpacing: "-0.2px",
                marginBottom: "10px",
              }}
            >
              {p.tag}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#B7C4BE",
                lineHeight: 1.6,
              }}
            >
              {p.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProductsSection = () => (
  <section
    className="cn-section cn-section-canvas"
    id="productos"
    data-testid="products-section"
  >
    <div className="cn-container">
      <div className="cn-section-head">
        <p className="cn-eyebrow" data-reveal>
          NUESTROS PRODUCTOS
        </p>
        <h2 className="cn-h2 cn-h2-sm" data-reveal data-testid="products-headline">
          Plataforma Caruna
        </h2>
      </div>
      <div className="cn-product-table" data-reveal>
        {PRODUCT_ROWS.map((row) => (
          <a
            key={row.name}
            href={row.href}
            className="cn-product-row"
            data-testid={`product-row-${row.name}`}
          >
            <span className="cn-product-name">{row.name}</span>
            <span className="cn-product-tech">
              {row.tech}
              <ArrowRight
                size={16}
                strokeWidth={2.4}
                className="cn-product-row-arrow"
                aria-hidden="true"
              />
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const HomePage = () => {
  useDocumentTitle(
    "Caruna — Cloud Voice Enablement Platform | Teams, Zoom, Webex",
  );
  const location = useLocation();

  // On mount or when hash changes, scroll to the requested section.
  useEffect(() => {
    if (location.hash) {
      // small delay so layout has rendered
      const id = setTimeout(() => scrollToHash(location.hash), 80);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [location.hash, location.key]);

  return (
    <>
      <Hero />
      <ConectorSection />
      <ConnectSection />
      <VoiceAISection />
      <InsightsSection />
      <ContactCenterSection />
      <NosotrosSection />
      <ModeloTrabajoSection />
      <ProductsSection />
      <Partner />
      <ContactForm />
    </>
  );
};

export default HomePage;
